import type { ComponentChildren, RefObject } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Check, ChevronDown, Search, X } from 'lucide-preact';
import { cn } from '@/lib/cn';
import { Popover } from '../overlays/Popover';
import { Icon } from './Icon';
import type { SelectOption } from './Select';

export type MultiSelectState = 'default' | 'error';

export interface MultiSelectProps<T extends string = string> {
  value: T[];
  options: SelectOption<T>[];
  onChange: (value: T[]) => void;
  placeholder?: string;
  searchable?: boolean;
  max?: number;
  disabled?: boolean;
  invalid?: boolean;
  state?: MultiSelectState;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  emptyMessage?: ComponentChildren;
  'aria-label'?: string;
}

const SIZE: Record<NonNullable<MultiSelectProps['size']>, string> = {
  sm: 'min-h-7 px-1.5 py-0.5 text-xs',
  md: 'min-h-9 px-2 py-1 text-13',
  lg: 'min-h-10 px-2.5 py-1.5 text-sm',
};

const STATE: Record<MultiSelectState, string> = {
  default: 'border-border hover:border-border-strong focus-within:border-brand focus-within:shadow-focus',
  error: 'border-error hover:border-error focus-within:border-error focus-within:shadow-focus-error',
};

export function MultiSelect<T extends string = string>({
  value,
  options,
  onChange,
  placeholder = 'Selecciona…',
  searchable = true,
  max,
  disabled = false,
  invalid = false,
  state,
  size = 'md',
  className,
  emptyMessage = 'Sin resultados',
  'aria-label': ariaLabel,
}: MultiSelectProps<T>) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const resolvedState = invalid ? 'error' : state ?? 'default';
  const st = STATE[resolvedState];

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const selected = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const text = (o.searchText ?? String(o.label ?? '') + ' ' + (o.description ?? '')).toLowerCase();
      return text.includes(q);
    });
  }, [query, options]);

  function toggle(opt: T) {
    if (value.includes(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else {
      if (max !== undefined && value.length >= max) return;
      onChange([...value, opt]);
    }
  }

  function remove(opt: T) {
    onChange(value.filter((v) => v !== opt));
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid}
        aria-label={ariaLabel}
        onClick={() => setOpen((p) => !p)}
        className={cn(
          'flex min-w-0 w-full flex-wrap items-center gap-1 rounded-md border bg-surface text-left text-fg transition-[border-color,box-shadow] duration-med ease-snappy focus-within:outline-none disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer',
          st,
          SIZE[size],
          className,
        )}
      >
        {selected.length === 0 ? (
          <span className="px-1 text-fg-faint">{placeholder}</span>
        ) : (
          selected.map((opt) => (
            <span
              key={opt.value}
              className="inline-flex items-center gap-1 rounded bg-brand-tint px-1.5 py-0.5 text-2xs font-medium text-brand"
            >
              {opt.label}
              {!disabled && (
                <span
                  role="button"
                  aria-label={`Quitar ${opt.value}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(opt.value);
                  }}
                  className="cursor-pointer rounded-sm hover:bg-brand-tint-strong"
                >
                  <X size={10} />
                </span>
              )}
            </span>
          ))
        )}
        <span className="ml-auto flex items-center pl-1 text-fg-faint">
          <Icon as={ChevronDown} size={14} className={cn('transition-transform duration-med', open && 'rotate-180')} />
        </span>
      </button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={triggerRef as RefObject<HTMLElement>}
        placement="bottom-start"
        matchAnchorWidth
        role="listbox"
        className="p-1"
      >
        {searchable ? (
          <div className="flex items-center gap-2 border-b border-border-subtle px-2 py-1.5">
            <Search size={14} className="text-fg-faint" />
            <input
              autofocus
              value={query}
              onInput={(e) => setQuery(e.currentTarget.value)}
              placeholder="Buscar…"
              className="w-full border-0 bg-transparent text-13 outline-none placeholder:text-fg-faint"
            />
          </div>
        ) : null}
        <div className="max-h-72 overflow-auto py-0.5">
          {filtered.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-fg-muted">{emptyMessage}</p>
          ) : (
            filtered.map((opt) => {
              const isSel = value.includes(opt.value);
              const reachedMax = !isSel && max !== undefined && value.length >= max;
              const disabled = opt.disabled || reachedMax;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  disabled={disabled}
                  onClick={() => !disabled && toggle(opt.value)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded text-left text-13 px-2 py-1.5 outline-none transition-colors duration-med cursor-pointer',
                    disabled && 'opacity-50 cursor-not-allowed',
                    !disabled && 'hover:bg-surface-2 focus-visible:bg-surface-2',
                  )}
                >
                  <span className="flex h-4 w-4 items-center justify-center rounded-xs border border-border-subtle">
                    {isSel ? <Icon as={Check} size={12} className="text-brand" /> : null}
                  </span>
                  <span className="flex-1 truncate">{opt.label}</span>
                </button>
              );
            })
          )}
          {max !== undefined ? (
            <p className="px-2 pb-1 pt-2 text-3xs text-fg-faint">
              {value.length} / {max} seleccionados
            </p>
          ) : null}
        </div>
      </Popover>
    </>
  );
}
